import React from 'react';
import { css, CSSProperties, StyleSheet } from 'aphrodite/no-important';
import { Logo, LogoProps } from '../Logo';
import { HeaderLinksDataProps } from '../Header';
import { useTheme } from '../../../../../theming/useTheme';
import { VrLineHeight } from '../../../../../theming/verticalRhythm/verticalRhythm';
import { SecondaryLinks } from './SecondaryLinks';
import { secondaryHeaderHeight } from '../header.utils';
import { AnimationDirection, AnimationDirectionType } from '../menuAnimations.util';
import { ErrorBoundary } from '../../../../errorHandling/ErrorBoundary';
import { MEDIA_BREAKPOINTS } from '../../../../../mediaQueries.const';

const componentConfigThemeKey = 'secondaryHeader';

export const maxVisibleLinksOnLargeAndXLarge = 6;

export interface SecondaryHeaderDataProps {
  secondaryLinks: HeaderLinksDataProps[] | null
  secondaryLogos: LogoProps | null
  moreLinksText: string,
  animation?: AnimationDirectionType,
  animationDuration?: number
    pagePath: string
}

export interface SecondaryHeaderThemeProps {
  vrLineHeight: VrLineHeight
  backgroundColor: string
  surfaceStyle: CSSProperties
}

export type SecondaryHeaderProps = SecondaryHeaderDataProps & SecondaryHeaderThemeProps;

type SecondaryHeaderStyle = {
  header: object,
  foldingUp: object,
  foldingDown: object,
};

const getStyle = (vrLineHeight: VrLineHeight, backgroundColor: string, animationDuration: number) => {
  return StyleSheet.create({
    header: {
      backgroundColor,
      position: 'relative',
      height: `${secondaryHeaderHeight}px`,
      display: 'grid',
      gridAutoFlow: 'column',
      width: '100%',
      alignItems: 'center',
      justifyContent: 'flex-start',
      zIndex: 50,
      transition: `${animationDuration}ms`,
      [MEDIA_BREAKPOINTS.X_LARGE]: {
        gridColumnGap: `${vrLineHeight[MEDIA_BREAKPOINTS.X_LARGE]}px`,
        paddingLeft: `${vrLineHeight[MEDIA_BREAKPOINTS.X_LARGE]}px`,
      },
      [MEDIA_BREAKPOINTS.LARGE]: {
        gridColumnGap: `${vrLineHeight[MEDIA_BREAKPOINTS.LARGE]}px`,
        paddingLeft: `${vrLineHeight[MEDIA_BREAKPOINTS.LARGE]}px`,
      },
      [MEDIA_BREAKPOINTS.MEDIUM]: {
        gridColumnGap: `${vrLineHeight[MEDIA_BREAKPOINTS.MEDIUM]}px`,
        paddingLeft: `${vrLineHeight[MEDIA_BREAKPOINTS.MEDIUM]}px`,
      },
      [MEDIA_BREAKPOINTS.SMALL]: {
        gridColumnGap: `${vrLineHeight[MEDIA_BREAKPOINTS.SMALL]}px`,
        paddingLeft: `${vrLineHeight[MEDIA_BREAKPOINTS.SMALL]}px`,
      },
    },
    foldingUp: {
      [MEDIA_BREAKPOINTS.MEDIUM]: {
        transform: 'translateY(-100%)',
      },
      [MEDIA_BREAKPOINTS.SMALL]: {
        transform: 'translateY(-100%)',
      },
    },
    foldingDown: {
      [MEDIA_BREAKPOINTS.MEDIUM]: {
        transform: 'translateY(0)',
        transitionDelay: '200ms',
      },
      [MEDIA_BREAKPOINTS.SMALL]: {
        transform: 'translateY(0)',
        transitionDelay: '200ms',
      },
    },
  });
};

const getClassWithAnimations = (animation: string, baseStyle: SecondaryHeaderStyle, surfaceStyle: { surfaceStyle: object }) => {
  switch (animation) {
    case AnimationDirection.UP: return css(baseStyle.header, surfaceStyle.surfaceStyle, baseStyle.foldingUp);
    case AnimationDirection.DOWN: return css(baseStyle.header, surfaceStyle.surfaceStyle, baseStyle.foldingDown);
    default: return css(baseStyle.header);
  }
};

export const SecondaryHeaderComponent: React.FunctionComponent<SecondaryHeaderProps> = props => {
  const { vrLineHeight, backgroundColor, surfaceStyle, secondaryLinks, secondaryLogos, moreLinksText, animationDuration = 0, animation = AnimationDirection.NONE, pagePath } = props;
  const style = getStyle(vrLineHeight, backgroundColor, animationDuration);
  const generatedSurfaceStyle = StyleSheet.create({ surfaceStyle });

  const finalStyle = getClassWithAnimations(animation, style, generatedSurfaceStyle as { surfaceStyle: object });
  return (
    <div className={finalStyle}>
      {secondaryLogos ? <Logo {...secondaryLogos} /> : null}
      {secondaryLinks ? <SecondaryLinks links={secondaryLinks} maxVisibleLinks={maxVisibleLinksOnLargeAndXLarge} moreLinksText={moreLinksText} pagePath={pagePath} /> : null}
    </div>
  );
};

const mapThemeProps = (theme: SecondaryHeaderThemeProps) => {
  return {
    vrLineHeight: theme.vrLineHeight,
    backgroundColor: theme.backgroundColor,
    surfaceStyle: theme.surfaceStyle,
  };
};

export const SecondaryHeader = (props: SecondaryHeaderDataProps) => {
  const themeContext = useTheme(componentConfigThemeKey, mapThemeProps);
  return <ErrorBoundary><SecondaryHeaderComponent {...themeContext} {...props} /></ErrorBoundary>;
};
